<template>
    <div class="max-w-4xl mx-auto pb-6 space-y-6">
        <h1 class="text-2xl font-bold text-gray-800">Limites de Uso</h1>

        <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div v-if="loading" class="space-y-4">
                <div class="skeleton h-5 w-48 rounded"></div>
                <div class="skeleton h-4 w-full rounded"></div>
                <div class="skeleton h-4 w-full rounded"></div>
            </div>

            <div v-else-if="errorMessage" class="text-sm text-red-600">{{ errorMessage }}</div>

            <div v-else class="space-y-6">
                <div class="flex items-center justify-between">
                    <p class="text-sm text-gray-500">
                        Plano <strong class="text-gray-700">{{ planLabel }}</strong> — os limites renovam em
                        <strong class="text-gray-700">{{ resetLabel }}</strong>
                    </p>
                </div>

                <UsageMeter
                    title="Relatórios com Inteligência Artificial"
                    :used="usage.standard.used"
                    :limit="usage.standard.limit"
                    :extra-credits="usage.standard.extraCredits"
                />

                <UsageMeter
                    title="Análises ACIA"
                    :used="usage.acia.used"
                    :limit="usage.acia.limit"
                    :extra-credits="usage.acia.extraCredits"
                />

                <div v-if="anyLimitReached" class="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-3">
                    <p class="text-sm text-amber-700">Você atingiu o limite do seu plano. Assine um plano superior para gerar mais relatórios.</p>
                    <router-link :to="{ name: 'subscription' }" class="btn btn-sm btn-primary text-white shrink-0">Ver planos</router-link>
                </div>
            </div>
        </section>

        <section v-if="!errorMessage" class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div class="flex items-center justify-between gap-3 mb-4">
                <div>
                    <h2 class="text-base font-semibold text-gray-800">Histórico de uso da IA</h2>
                    <p class="text-sm text-gray-500">Registros do ciclo atual, 10 por página, mais recentes primeiro.</p>
                </div>
                <span v-if="loadingHistory" class="text-xs font-semibold text-cyan-600">Carregando página...</span>
            </div>

            <div v-if="loading" class="space-y-3">
                <div v-for="i in 3" :key="i" class="rounded-xl border border-gray-100 p-4 space-y-3">
                    <div class="skeleton h-4 w-28 rounded"></div>
                    <div class="skeleton h-4 w-full rounded"></div>
                    <div class="skeleton h-4 w-2/3 rounded"></div>
                    <div class="skeleton h-4 w-40 rounded"></div>
                </div>
            </div>

            <div v-else class="space-y-4">
                <div v-if="historyErrorMessage" class="text-sm text-red-600">{{ historyErrorMessage }}</div>

                <div
                    v-if="history.items.length"
                    class="space-y-3"
                    :class="{ 'opacity-60 pointer-events-none': loadingHistory }"
                >
                    <article
                        v-for="item in history.items"
                        :key="item.uuid"
                        class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-4"
                    >
                        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div class="space-y-2 min-w-0">
                                <div class="inline-flex rounded-full bg-cyan-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-700">
                                    {{ historyTypeLabel(item) }}
                                </div>
                                <p class="text-sm font-semibold text-gray-800 break-words">
                                    Paciente: {{ item.patient_name || 'Paciente não identificado' }}
                                </p>
                                <p class="text-sm text-gray-600 break-words">
                                    Usuário: {{ item.user_name || 'Usuário não identificado' }}
                                </p>
                            </div>

                            <div class="text-sm text-gray-500 shrink-0">
                                {{ formatHistoryDate(item.created_at) }}
                            </div>
                        </div>
                    </article>
                </div>

                <div v-else class="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                    Nenhum uso de IA neste ciclo até agora.
                </div>

                <div class="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                    <button
                        class="btn btn-sm btn-outline"
                        :disabled="loadingHistory || !canGoPrevious"
                        @click="goToPage(history.page - 1)"
                    >
                        Anterior
                    </button>

                    <span class="text-sm text-gray-500">
                        Página {{ history.page }} de {{ history.totalPages }}
                    </span>

                    <button
                        class="btn btn-sm btn-outline"
                        :disabled="loadingHistory || !canGoNext"
                        @click="goToPage(history.page + 1)"
                    >
                        Próxima
                    </button>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { UsageService } from '@/services/Entity';
import { PLANS } from '@/constants/plans';
import UsageMeter from '@/components/UsageMeter.vue';
import { dateTime } from '@/filters/date';

const HISTORY_LIMIT = 10;

function emptyUsageState() {
    return {
        plan: 'basico',
        period: { start: null, end: null },
        standard: { used: 0, limit: 0, extraCredits: 0 },
        acia: { used: 0, limit: 0, extraCredits: 0 }
    };
}

function emptyHistoryState() {
    return {
        items: [],
        page: 1,
        limit: HISTORY_LIMIT,
        total: 0,
        totalPages: 1
    };
}

function normalizeHistory(history) {
    return {
        items: Array.isArray(history?.items) ? history.items : [],
        page: Number(history?.page) > 0 ? Number(history.page) : 1,
        limit: Number(history?.limit) > 0 ? Number(history.limit) : HISTORY_LIMIT,
        total: Number(history?.total) >= 0 ? Number(history.total) : 0,
        totalPages: Math.max(1, Number(history?.totalPages) > 0 ? Number(history.totalPages) : 1)
    };
}

export default {
    components: { UsageMeter },
    setup() {
        const loading = ref(true);
        const loadingHistory = ref(false);
        const errorMessage = ref('');
        const historyErrorMessage = ref('');
        const usage = ref(emptyUsageState());
        const history = ref(emptyHistoryState());

        async function loadUsage(page = 1, options = {}) {
            const keepCurrentUsage = Boolean(options.keepCurrentUsage);

            if (keepCurrentUsage) {
                loadingHistory.value = true;
                historyErrorMessage.value = '';
            } else {
                loading.value = true;
                errorMessage.value = '';
                historyErrorMessage.value = '';
            }

            try {
                const { data } = await UsageService.getUsage({
                    include_history: true,
                    page,
                    limit: HISTORY_LIMIT
                });
                const payload = (data && data.data) || null;

                if (!payload) throw new Error('missing_usage_data');

                usage.value = {
                    ...emptyUsageState(),
                    ...payload
                };
                history.value = normalizeHistory(payload.history);
            } catch (e) {
                if (keepCurrentUsage) {
                    historyErrorMessage.value = 'Não foi possível carregar o histórico de uso da IA. Tente novamente.';
                } else {
                    errorMessage.value = 'Não foi possível carregar seus limites de uso. Tente novamente.';
                }
            } finally {
                if (keepCurrentUsage) {
                    loadingHistory.value = false;
                } else {
                    loading.value = false;
                }
            }
        }

        onMounted(() => {
            loadUsage(1);
        });

        const planLabel = computed(() => {
            const found = PLANS.find(p => p.id === usage.value.plan);
            return found ? found.name : usage.value.plan;
        });

        const resetLabel = computed(() => {
            if (!usage.value.period || !usage.value.period.start) return '—';
            const start = new Date(usage.value.period.start);
            const nextMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
            return nextMonth.toLocaleDateString('pt-BR');
        });

        const anyLimitReached = computed(() => {
            const s = usage.value.standard;
            const a = usage.value.acia;
            return (s.used >= s.limit + s.extraCredits) || (a.used >= a.limit + a.extraCredits);
        });

        const canGoPrevious = computed(() => history.value.page > 1);
        const canGoNext = computed(() => history.value.page < history.value.totalPages);

        function goToPage(page) {
            if (loading.value || loadingHistory.value) return;
            if (page < 1 || page > history.value.totalPages || page === history.value.page) return;
            loadUsage(page, { keepCurrentUsage: true });
        }

        function historyTypeLabel(item) {
            return item?.type === 'acia' ? 'ACIA' : 'Conclusões';
        }

        function formatHistoryDate(value) {
            return value ? dateTime(value) : 'Data indisponível';
        }

        return {
            loading,
            loadingHistory,
            errorMessage,
            historyErrorMessage,
            usage,
            history,
            planLabel,
            resetLabel,
            anyLimitReached,
            canGoPrevious,
            canGoNext,
            goToPage,
            historyTypeLabel,
            formatHistoryDate
        };
    }
};
</script>
